import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import logoUrl from '../../assets/arms-logo.png';

export type AuthShellProps = {
  /** Headline above the form, e.g. "Sign in" or "Welcome to ARMS". */
  title: string;
  /** Caption under the headline. */
  subtitle?: string;
  /** Slot under subtitle, above the form (stepper, alert, etc.). */
  topSlot?: ReactNode;
  children: ReactNode;
};

/**
 * Split-screen auth chrome. Brand panel on the left (gradient + the
 * ARMS logo + tagline); form panel on the right. Collapses to a tight
 * single-column on viewports under md so the form always fits in one
 * viewport without scrolling.
 *
 * The logo PNG already contains the "ARMS" wordmark, so the brand
 * panel doesn't repeat it as text — just the logo + a tagline.
 */
export function AuthShell({ title, subtitle, topSlot, children }: AuthShellProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const accent  = theme.palette.secondary.main;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: 'background.default',
      }}
    >
      {/* ---------- BRAND PANEL ---------- */}
      <Box
        sx={{
          width:    { xs: '100%', md: '45%' },
          minHeight: { xs: 'auto', md: '100vh' },
          py:       { xs: 3,      md: 6 },
          px:       { xs: 3,      md: 6 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', md: 'space-between' },
          alignItems: { xs: 'center', md: 'flex-start' },
          color: '#fff',
          position: 'relative',
          background: isDark
            ? `radial-gradient(circle at 20% 0%, ${alpha(accent, 0.40)} 0%, transparent 50%),
               linear-gradient(160deg, #0a1024 0%, ${primary} 60%, ${accent} 110%)`
            : `radial-gradient(circle at 20% 0%, ${alpha(accent, 0.25)} 0%, transparent 55%),
               linear-gradient(160deg, ${primary} 0%, ${darken(accent, 0.05)} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Logo only — the PNG already says "ARMS". On mobile it's a
            small bar at the top; on desktop it sits in the upper-left. */}
        <Box
          component="img"
          src={logoUrl}
          alt="ARMS"
          sx={{
            width:  { xs: 64, md: 120 },
            height: { xs: 64, md: 120 },
            borderRadius: 2,
            backgroundColor: '#fff',
            p: 0.75,
            boxShadow: '0 8px 24px rgba(0,0,0,0.30)',
            position: 'relative',
            zIndex: 1,
          }}
        />

        {/* Tagline — middle of the brand panel on desktop, hidden on
            mobile so the brand bar stays compact. */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.3, opacity: 0.95, maxWidth: 420 }}>
            Operator-grade gaming-route management — sites, machines,
            cashouts and approvals in one place.
          </Typography>
        </Box>

        {/* Footer — desktop only. */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', zIndex: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            © {new Date().getFullYear()} ACES — ARMS Platform
          </Typography>
        </Box>
      </Box>

      {/* ---------- FORM PANEL ---------- */}
      <Box
        sx={{
          width:  { xs: '100%', md: '55%' },
          minHeight: { xs: 'auto', md: '100vh' },
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Stack spacing={3} sx={{ width: '100%', maxWidth: 440 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {topSlot}

          {children}
        </Stack>
      </Box>
    </Box>
  );
}

// ---------- tiny color helpers ----------

function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }

function parseHex(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  const v = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
}

function alpha(hex: string, a: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(a, 0, 1)})`;
}

function darken(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const f = clamp(1 - amount, 0, 1);
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}
